import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './HRCreditConversion.css';

// HR screen: convert banked late-worker credit hours into casual leave.
// 9 hours of credit = 1 casual day. The server does all the real work
// (and re-checks everything) — this screen only shows balances and asks
// for a confirmed click. It does not touch attendance in any way.

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1]?.[0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

function fmtHM(minutes) {
  const m = Math.max(0, parseInt(minutes) || 0);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-PK', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function HRCreditConversion() {
  const [data, setData]       = useState({ minutes_per_day: 540, candidates: [], history: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('ready'); // ready | all
  const [confirm, setConfirm] = useState(null);    // { emp, days }
  const [saving, setSaving]   = useState(false);
  const [modalErr, setModalErr] = useState('');
  const [toast, setToast]     = useState('');

  const perDay = data.minutes_per_day || 540;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res  = await fetch(`${BASE}/credit-conversion/candidates`, { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) { setError(json.error || 'Failed to load'); }
      else setData(json);
    } catch (e) {
      setError('Could not reach the server');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.candidates.filter(c => {
      if (filter === 'ready' && !(c.convertible_days > 0)) return false;
      if (!q) return true;
      return (c.full_name || '').toLowerCase().includes(q) || (c.emp_code || '').toLowerCase().includes(q);
    });
  }, [data.candidates, search, filter]);

  const totalCredit = data.candidates.reduce((s, c) => s + c.credit_minutes, 0);
  const readyCount  = data.candidates.filter(c => c.convertible_days > 0 && !c.on_probation).length;

  const openConfirm = (emp) => { setModalErr(''); setConfirm({ emp, days: 1 }); };

  const doConvert = async () => {
    if (!confirm || saving) return;
    setSaving(true); setModalErr('');
    try {
      const res  = await fetch(`${BASE}/credit-conversion/convert`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ employee_id: confirm.emp.id, days: confirm.days }),
      });
      const json = await res.json();
      if (!res.ok) { setModalErr(json.error || 'Conversion failed'); }
      else {
        setConfirm(null);
        showToast(`✓ ${json.message}`);
        fetchData();
      }
    } catch (e) {
      setModalErr('Could not reach the server. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cc-root">
      <div className="cc-header">
        <div>
          <h2 className="cc-title">Credit → Casual Leave</h2>
          <p className="cc-sub">
            Staff who work past their shift bank credit hours. Every <b>{perDay / 60} hours</b> of
            credit can be converted into <b>1 casual leave day</b>. The credit is used up and the
            casual day is added to their account from this month onward. Attendance, past months and
            salary already calculated are not changed.
          </p>
        </div>
        <button className="cc-refresh" onClick={fetchData}>↻ Refresh</button>
      </div>

      {error && <div className="cc-banner cc-banner-red">⚠ {error}</div>}
      {toast && <div className="cc-toast">{toast}</div>}

      <div className="cc-stats">
        <div className="cc-stat">
          <span className="cc-stat-num">{data.candidates.length}</span>
          <span className="cc-stat-lbl">Late workers</span>
        </div>
        <div className="cc-stat cc-stat-green">
          <span className="cc-stat-num">{readyCount}</span>
          <span className="cc-stat-lbl">Ready to convert</span>
        </div>
        <div className="cc-stat">
          <span className="cc-stat-num">{fmtHM(totalCredit)}</span>
          <span className="cc-stat-lbl">Credit held in total</span>
        </div>
      </div>

      <div className="cc-toolbar">
        <input
          className="cc-search"
          placeholder="Search name or ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="cc-seg">
          <button className={filter === 'ready' ? 'on' : ''} onClick={() => setFilter('ready')}>Ready ({data.candidates.filter(c => c.convertible_days > 0).length})</button>
          <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>All ({data.candidates.length})</button>
        </div>
      </div>

      {loading ? (
        <div className="cc-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="cc-empty">
          {filter === 'ready'
            ? 'Nobody has 9 hours of credit yet.'
            : 'No matching employees.'}
        </div>
      ) : (
        <div className="cc-list">
          {rows.map(c => {
            const pct = Math.min(100, Math.round(((c.credit_minutes % perDay || (c.convertible_days > 0 ? perDay : 0)) / perDay) * 100));
            const blocked = c.on_probation;
            const canConvert = c.convertible_days > 0 && !blocked;
            return (
              <div className="cc-card" key={c.id}>
                <div className="cc-avatar" style={{ background: getAvatarColor(c.full_name) }}>{getInitials(c.full_name)}</div>
                <div className="cc-who">
                  <div className="cc-name">{c.full_name}</div>
                  <div className="cc-code">{c.emp_code}{c.city ? ` · ${c.city}` : ''}</div>
                  {blocked && <div className="cc-tag cc-tag-purple">On probation until {c.probation_ends}</div>}
                </div>
                <div className="cc-credit">
                  <div className="cc-credit-num">{fmtHM(c.credit_minutes)}</div>
                  <div className="cc-bar"><div className="cc-bar-fill" style={{ width: `${pct}%` }} /></div>
                  <div className="cc-credit-sub">
                    {c.convertible_days > 0
                      ? `${c.convertible_days} day${c.convertible_days > 1 ? 's' : ''} ready · ${fmtHM(c.leftover_minutes)} left over`
                      : `${fmtHM(perDay - c.credit_minutes)} more to reach 1 day`}
                  </div>
                </div>
                <div className="cc-casual">
                  <div className="cc-casual-num">{c.casual_remaining}<span> / {c.casual_total}</span></div>
                  <div className="cc-credit-sub">
                    casual left{c.casual_from_credit > 0 ? ` · +${c.casual_from_credit} from credit` : ''}
                  </div>
                </div>
                <button
                  className="cc-btn"
                  disabled={!canConvert}
                  title={blocked ? 'On probation — no leave entitlement yet' : (!canConvert ? 'Needs 9 hours of credit' : '')}
                  onClick={() => openConfirm(c)}
                >
                  Convert → Casual
                </button>
              </div>
            );
          })}
        </div>
      )}

      <h3 className="cc-h3">Recent conversions</h3>
      {data.history.length === 0 ? (
        <div className="cc-empty cc-empty-sm">No conversions yet.</div>
      ) : (
        <div className="cc-hist">
          <table>
            <thead>
              <tr><th>When</th><th>Employee</th><th className="cc-r">Days added</th><th className="cc-r">Credit used</th><th>Effective</th><th>By</th></tr>
            </thead>
            <tbody>
              {data.history.map(h => (
                <tr key={h.id}>
                  <td>{fmtDateTime(h.created_at)}</td>
                  <td>{h.full_name} <span className="cc-code">{h.emp_code}</span></td>
                  <td className="cc-r">+{parseFloat(h.days)}</td>
                  <td className="cc-r">{fmtHM(h.minutes_converted)}</td>
                  <td>{MONTHS[h.month - 1]} {h.year} →</td>
                  <td>{h.granted_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <div className="cc-overlay" onClick={() => !saving && setConfirm(null)}>
          <div className="cc-modal" onClick={e => e.stopPropagation()}>
            <h3>Convert credit to casual leave?</h3>
            <p className="cc-modal-who"><b>{confirm.emp.full_name}</b> <span className="cc-code">{confirm.emp.emp_code}</span></p>

            {confirm.emp.convertible_days > 1 && (
              <div className="cc-days">
                <span>Days to convert</span>
                <div className="cc-step">
                  <button disabled={confirm.days <= 1 || saving} onClick={() => setConfirm({ ...confirm, days: confirm.days - 1 })}>−</button>
                  <b>{confirm.days}</b>
                  <button disabled={confirm.days >= confirm.emp.convertible_days || saving} onClick={() => setConfirm({ ...confirm, days: confirm.days + 1 })}>+</button>
                </div>
              </div>
            )}

            <div className="cc-sum">
              <div><span>Credit used</span><b>− {fmtHM(confirm.days * perDay)}</b></div>
              <div><span>Credit left after</span><b>{fmtHM(confirm.emp.credit_minutes - confirm.days * perDay)}</b></div>
              <div><span>Casual leave added</span><b className="cc-green">+ {confirm.days} day{confirm.days > 1 ? 's' : ''}</b></div>
              <div><span>Casual balance after</span><b>{confirm.emp.casual_remaining + confirm.days} / {confirm.emp.casual_total + confirm.days}</b></div>
            </div>
            <p className="cc-warn">This cannot be undone from this screen, and the credit can no longer be used to forgive lateness.</p>

            {modalErr && <div className="cc-banner cc-banner-red">⚠ {modalErr}</div>}

            <div className="cc-modal-actions">
              <button className="cc-cancel" disabled={saving} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="cc-go" disabled={saving} onClick={doConvert}>{saving ? 'Converting…' : 'Confirm conversion'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}